/**
 * 学习乐园 · 数字华容道（game-huarong.js）
 * ============================================================
 * 「排序与序列」组第二作：n×n 网格里 1..n²−1 块滑回顺序，经典滑块拼图。
 * 交互与排排序同风格（点选、无拖拽）：点空格旁边（同行或同列）的数字块，
 * 块就滑进空格；与空格同行列但隔着的块可以连着推一串——经典桌面手感，
 * 鼠标与触屏一致（触屏不拖拽的红线与排排序相同）。
 *
 * 可解性口径（本作的核心教学点）：打乱一律从「已解局面」倒着随机走合法步
 * 生成——走出来的局面天然可解，页面如实说明；而 4×4 随便乱拨的经典玩具
 * 有一半局面永远解不开（逆序数奇偶性），FAQ 与 doc 里讲清为什么。
 *
 * 口径：
 *   - 三档 3×3（默认，铁律：默认档必须最简单）/ 4×4 / 5×5，成绩按档分开存本机；
 *   - 只计步数不计时：华容道练的是「空格怎么借」的思路，不是手速；
 *   - 不做撤销：滑错一格的成本是一滑的代价，撤销按钮省下的思考大于付出。
 *
 * 冒烟钩子：window.STHuarong 暴露纯引擎（levelSpec / scramble / slide /
 * isSolved / inversions / solvable），供单测与冒烟跑确定性用例。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-huarong';
  var RECORD_KEY = 'study-huarong-record';

  var LEVELS = {
    3: { label: '入门 3×3', hint: '默认档 · 9 格', steps: 50 },
    4: { label: '经典 4×4', hint: '16 格', steps: 120 },
    5: { label: '挑战 5×5', hint: '25 格', steps: 200 },
  };

  /* ============ 纯引擎（无 DOM） ============ */

  function levelSpec(n) {
    var lv = LEVELS[n] ? n : 3;
    return { n: lv, steps: LEVELS[lv].steps };
  }

  // 已解局面：1..n²−1，末位空格（0）
  function solved(n) {
    var b = [];
    for (var i = 0; i < n * n; i++) b.push((i + 1) % (n * n));
    return b;
  }

  function isSolved(board) {
    var n = Math.round(Math.sqrt(board.length));
    for (var i = 0; i < n * n - 1; i++) {
      if (board[i] !== i + 1) return false;
    }
    return board[n * n - 1] === 0;
  }

  // 空格的邻居（上下左右合法位）
  function neighbors(n, blank) {
    var r = Math.floor(blank / n), c = blank % n, out = [];
    if (r > 0) out.push(blank - n);
    if (r < n - 1) out.push(blank + n);
    if (c > 0) out.push(blank - 1);
    if (c < n - 1) out.push(blank + 1);
    return out;
  }

  /**
   * 打乱：从已解局面倒着随机走 steps 步合法滑步（不立即回退上一步）。
   * 走出来的局面保证可解（可解性由构造保证，不靠事后判定）。
   * 末态若恰好已解（概率≈0）则再走一轮兜底。
   */
  function scramble(n, steps) {
    var board = solved(n);
    var blank = n * n - 1;
    var prev = -1;
    // 步数下限 1：0 步走不出「非已解」局面，兜底至少走一步（一步必然打乱且可解）
    var real = Math.max(1, steps | 0);
    for (var round = 0; round < 2; round++) {
      for (var i = 0; i < real; i++) {
        var cand = neighbors(n, blank).filter(function (x) { return x !== prev; });
        var pick = cand[Math.floor(Math.random() * cand.length)];
        board[blank] = board[pick];
        board[pick] = 0;
        prev = blank;
        blank = pick;
      }
      if (!isSolved(board)) break;
    }
    return board;
  }

  /**
   * 滑块（纯判定，供冒烟复用）：idx 与空格同行或同列才推得动——
   * 中间的块连同它一起向空格方向推一格（连推），count = 推动的块数。
   * 非法时 why 是给用户看的解释——解释规则，不是报错。
   */
  function slide(board, idx) {
    var n = Math.round(Math.sqrt(board.length));
    var blank = board.indexOf(0);
    if (idx < 0 || idx >= board.length) return { ok: false, why: '这一格不在棋盘上。' };
    if (idx === blank) return { ok: false, why: '点中的是空格本身——空格不用动，点它旁边的数字块。' };
    var sameRow = Math.floor(idx / n) === Math.floor(blank / n);
    var sameCol = idx % n === blank % n;
    if (!sameRow && !sameCol) {
      return {
        ok: false,
        why: '「' + board[idx] + '」和空格既不同行也不同列，中间隔着别的块，推不过去。'
          + '只有和空格在同一行或同一列的块能滑——先挪开路上的块，给它借出一条道。',
      };
    }
    var next = sameRow ? (idx > blank ? 1 : -1) : (idx > blank ? n : -n);
    var out = board.slice();
    var pos = blank, count = 0;
    while (pos !== idx) {
      var p = pos + next;
      out[pos] = out[p];
      out[p] = 0;
      pos = p;
      count++;
    }
    return { ok: true, why: '', board: out, count: count };
  }

  // 逆序数：忽略空格后的序列里，前大后小的对数（可解性科普用）
  function inversions(board) {
    var seq = board.filter(function (x) { return x !== 0; });
    var inv = 0;
    for (var i = 0; i < seq.length; i++) {
      for (var j = i + 1; j < seq.length; j++) {
        if (seq[i] > seq[j]) inv++;
      }
    }
    return inv;
  }

  // 可解性判定（科普口径）：宽为奇 → 逆序数为偶即可解；
  // 宽为偶 → 逆序数 + 空格所在行（自底向上数，1 起）为奇即可解。
  function solvable(board) {
    var n = Math.round(Math.sqrt(board.length));
    var inv = inversions(board);
    if (n % 2 === 1) return inv % 2 === 0;
    var rowFromBottom = n - Math.floor(board.indexOf(0) / n);
    return (inv + rowFromBottom) % 2 === 1;
  }

  /* ============ UI：状态与工具 ============ */

  var root = null;
  var ui = {};
  var S = {
    level: 3, board: [], moves: 0, done: false,
    soundOn: true, resetArm: false, resetTimer: null,
  };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* —— 音效（WebAudio 本地合成，与汉诺塔同一套音色思路）—— */
  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0]];
      else if (kind === 'bad') seq = [[200, 0]];
      else seq = [[523, 0], [659, 0.09], [784, 0.18]];   // win
      var type = kind === 'bad' ? 'square' : 'sine';
      for (var i = 0; i < seq.length; i++) {
        var osc = actx.createOscillator();
        var g = actx.createGain();
        osc.type = type;
        osc.frequency.value = SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0];
        var t0 = t + seq[i][1];
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.27 : 0.09, t0 + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + (kind === 'win' && i === seq.length - 1 ? (SFX_MOBILE ? 0.32 : 0.24) : (SFX_MOBILE ? 0.16 : 0.1)));
        osc.connect(g).connect(actx.destination);
        osc.start(t0); osc.stop(t0 + (SFX_MOBILE ? 0.4 : 0.3));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* —— 偏好与成绩（只存本机）—— */
  function save() {
    try { localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, level: S.level, soundOn: S.soundOn })); }
    catch (e) { /* 存储不可用静默 */ }
  }
  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (d && d.v === 1) {
        if (LEVELS[d.level]) S.level = d.level;
        S.soundOn = d.soundOn !== false;
      }
    } catch (e) { /* 忽略脏数据 */ }
  }
  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  /* ============ 渲染 ============ */

  function renderBoard() {
    var n = S.level;
    ui.board.textContent = '';
    ui.board.style.setProperty('--st-hr-cols', String(n));
    ui.tiles = [];
    for (var i = 0; i < n * n; i++) {
      (function (idx) {
        var num = S.board[idx];
        var node;
        if (num === 0) {
          node = el('div', 'st-hr-gap');
          node.setAttribute('aria-label', '空格');
        } else {
          node = el('button', 'st-hr-tile');
          node.type = 'button';
          node.textContent = String(num);
          // 归位标记：数字站上自己的格子（1 在第 1 格……）给个淡标，玩家自己看进度
          if (num === idx + 1) node.classList.add('is-home');
          node.setAttribute('aria-label', '数字 ' + num + (num === idx + 1 ? '，已归位' : '')
            + (canSlide(idx) ? '，可以滑进空格' : '，与空格不同行不同列，现在推不动'));
          node.addEventListener('click', function () { onTile(idx); });
        }
        ui.board.appendChild(node);
        ui.tiles.push(node);
      })(i);
    }
  }

  function canSlide(idx) {
    var n = S.level;
    var blank = S.board.indexOf(0);
    return idx === blank + n || idx === blank - n
      || (idx === blank + 1 && idx % n !== 0)
      || (idx === blank - 1 && blank % n !== 0);
  }

  function renderStatus() {
    if (S.done) return;
    ui.status.textContent = '步数 ' + S.moves + ' · 点空格旁边的数字块，把它滑进空格';
  }

  function renderNote(msg, tone) {
    ui.note.textContent = msg;
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    var any = [3, 4, 5].some(function (lv) { return rec[lv] && rec[lv].bestMoves != null; });
    if (!any) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty', '还没通关过——把数字滑回顺序，这里按档位记下你的最少步数。'));
      return;
    }
    box.className = 'st-llk-record';
    [3, 4, 5].forEach(function (lv) {
      var r = rec[lv];
      box.appendChild(recordStat(lv + '×' + lv + '最少',
        r && r.bestMoves != null ? r.bestMoves + ' 步' : '—'));
    });
  }

  function renderLevels() {
    ui.levels.textContent = '';
    [3, 4, 5].forEach(function (lv) {
      var b = el('button', 'st-llk-pack-btn' + (S.level === lv ? ' is-active' : ''));
      b.type = 'button';
      b.setAttribute('data-level', String(lv));
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', S.level === lv ? 'true' : 'false');
      b.appendChild(el('span', 'st-llk-pack-name', LEVELS[lv].label));
      b.appendChild(el('small', 'st-llk-pack-hint', LEVELS[lv].hint));
      b.addEventListener('click', function () {
        if (S.level === lv) return;
        S.level = lv;
        renderLevels();
        newGame();
        save();
      });
      ui.levels.appendChild(b);
    });
  }

  function renderButtons() {
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  function flashBad(idxs) {
    for (var i = 0; i < idxs.length; i++) {
      var t = ui.tiles[idxs[i]];
      if (t && t.classList) t.classList.add('is-bad');
    }
    setTimeout(function () {
      for (var j = 0; j < idxs.length; j++) {
        var b = ui.tiles[idxs[j]];
        if (b && b.isConnected) b.classList.remove('is-bad');
      }
    }, 420);
  }

  /* ============ 交互 ============ */

  function onTile(idx) {
    if (S.done) return;
    var r = slide(S.board, idx);
    if (!r.ok) {
      renderNote(r.why, 'warn');
      sfx('bad');
      var num = S.board[idx];
      if (num) flashBad([idx]);
      return;
    }
    S.board = r.board;
    S.moves += r.count;
    sfx('ok');
    if (isSolved(S.board)) { renderBoard(); finish(); return; }
    renderBoard();
    renderStatus();
  }

  /* 键盘：方向键 = 让对应方向的块滑进空格（按←是空格右边的块向左滑） */
  function bindKeys() {
    global.addEventListener('keydown', function (e) {
      if (S.done) return;
      var n = S.level;
      var blank = S.board.indexOf(0);
      var map = { ArrowLeft: blank + 1, ArrowRight: blank - 1, ArrowUp: blank + n, ArrowDown: blank - n };
      var idx = map[e.key];
      if (idx == null) return;
      var r = slide(S.board, idx);
      if (!r.ok) return;   // 越界/换行越位：忽略，不响错误音（键盘操作本就是顺手的补充）
      e.preventDefault();
      onTile(idx);
    });
  }

  function finish() {
    S.done = true;
    var rec = loadRecord();
    var r = rec[S.level] || { plays: 0, wins: 0, bestMoves: null };
    r.plays++;
    r.wins++;
    var isBest = r.bestMoves == null || S.moves < r.bestMoves;
    if (isBest) r.bestMoves = S.moves;
    rec[S.level] = r;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 静默 */ }
    ui.status.textContent = '完成！' + S.moves + ' 步，数字全部归位。';
    ui.status.className = 'st-status is-over';
    var teach = '数字华容道没有统一的「最少步数」公式，如实说：' + S.moves + ' 步就是这一局的成绩。'
      + '省步的关键在空格——它不是障碍，是你唯一的「手」：想让谁动，先把空格借到它旁边。'
      + '常用思路是降阶法：先把第 1 行和第 1 列归位，剩下的就是小一号的华容道。'
      + (isBest ? '这是 ' + S.level + '×' + S.level + ' 档的最好成绩！' : '');
    renderNote(teach, 'ok');
    sfx('win');
    renderRecord();
    renderButtons();
  }

  function newGame() {
    var spec = levelSpec(S.level);
    S.level = spec.n;
    S.board = scramble(spec.n, spec.steps);
    S.moves = 0;
    S.done = false;
    ui.status.className = 'st-status';
    renderStatus();
    renderNote('把数字滑回 1 到 ' + (S.level * S.level - 1) + '：点空格旁边（同行或同列）的块就滑过去，'
      + '隔着的块会连着推。这局是从解开的局面倒着打乱的，一定能解出来。', '');
    renderBoard();
    renderRecord();
    renderButtons();
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.setItem(RECORD_KEY, JSON.stringify({})); } catch (e) { /* 静默 */ }
    renderRecord();
    renderButtons();
    renderNote('已重置数字华容道的本机成绩。', '');
  }

  /* 音效选择器（口径同汉诺塔） */
  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          syncSoundPicker();
          save();
          if (S.soundOn) sfx('ok');
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }
  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.board = root.querySelector('[data-st-hr-board]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.levels = root.querySelector('[data-st-hr-levels]');
    ui.record = root.querySelector('[data-st-record]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.resetBtn = root.querySelector('[data-st-reset]');
    if (!ui.board) return;

    restorePrefs();
    renderLevels();
    bindSoundPicker();
    bindKeys();

    ui.newBtn.addEventListener('click', function () { newGame(); });
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与单测钩子 ============ */

  global.STHuarong = {
    levelSpec: levelSpec,
    solved: solved,
    scramble: scramble,
    slide: slide,
    isSolved: isSolved,
    inversions: inversions,
    solvable: solvable,
    neighbors: neighbors,
  };
})(typeof window !== 'undefined' ? window : this);
